import { Injectable } from '@angular/core';
import { DeclineReasons } from '@galaxy/billing';
import { AppType, MarketplaceAppService } from '@galaxy/marketplace-apps/v1';
import { TranslateService } from '@ngx-translate/core';
import { Account, PendingActivation, PendingActivationStatus } from '@vendasta/accounts/legacy';
import { OrderFulfillmentService, OrderItem, OrderItemStatus, ValidationErrorCodes } from '@vendasta/order-fulfillment';
import { Cursor, PagedOrderItemsResponse } from '@vendasta/order-fulfillment/lib/order-fulfillment.service';
import { combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';
import { AccountService } from '../../core/account.service';

/**
 * Describes the steps in the pending activation process.
 * Used as additional information for the general "pending" status.
 */
export enum PendingActivationStep {
  WAITING_ON_VENDOR = 0,
  VALIDATION_FAILED,
  CHARGE_FAILED,
  ACTIVATION_FAILED,
}

/**
 * The mapping of PendingActivationStep to the message to display to the user.
 */
export const PendingActivationStepMessages = {
  // Currently, we only show a special message when waiting on the vendor
  [PendingActivationStep.WAITING_ON_VENDOR]: 'Pending vendor approval',
};

export interface PendingActivationDisplay {
  activationId: string;
  businessId: string;
  createdDate: Date;
  editionId?: string;
  itemId: string;
  orderFormSubmissionId: string;
  status: PendingActivationStatus;
  pendingActivationStep?: PendingActivationStep;
  updatedDate?: Date;
  rejectedReason?: string;
  orderId?: string;
  orderItemId?: string;
  appId?: string;
  addonId?: string;
}

// List containing all valid pending order item statuses
const OrderItemPendingStatuses: OrderItemStatus[] = [
  OrderItemStatus.ORDER_ITEM_STATUS_CREATED,
  OrderItemStatus.ORDER_ITEM_STATUS_CHARGED,
  OrderItemStatus.ORDER_ITEM_STATUS_CHARGE_SUCCESSFUL,
  OrderItemStatus.ORDER_ITEM_STATUS_PENDING_ACTIVATION,
];

// List containing all valid rejected order item statuses
const OrderItemRejectedStatuses: OrderItemStatus[] = [
  OrderItemStatus.ORDER_ITEM_STATUS_VALIDATION_FAILED,
  OrderItemStatus.ORDER_ITEM_STATUS_CHARGE_FAILED,
];

// Map of Order Item Statuses to user facing text
const RejectedOrderItemText: Map<OrderItemStatus, string> = new Map([
  [OrderItemStatus.ORDER_ITEM_STATUS_VALIDATION_FAILED, 'There was an error activating the product.'],
  [OrderItemStatus.ORDER_ITEM_STATUS_CHARGE_FAILED, 'There was an error charging for the product.'],
]);

// Map of Order Item Validation Error Codes to user facing text
const ValidationErrorOrderItemText: Map<ValidationErrorCodes, string> = new Map([
  [
    ValidationErrorCodes.VALIDATION_ERROR_CODES_INVALID_ENTRY_URL,
    'ACCOUNTS.VALIDATION_ERROR_CODE_MESSAGES.INVALID_ENTRY_URL',
  ],
  [
    ValidationErrorCodes.VALIDATION_ERROR_CODES_ENTRY_URL_NOT_PROVIDED,
    'ACCOUNTS.VALIDATION_ERROR_CODE_MESSAGES.URL_NOT_PROVIDED',
  ],
  [
    ValidationErrorCodes.VALIDATION_ERROR_CODES_INVALID_DEPENDENCY,
    'ACCOUNTS.VALIDATION_ERROR_CODE_MESSAGES.INVALID_DEPENDENCY',
  ],
  [
    ValidationErrorCodes.VALIDATION_ERROR_CODES_ALREADY_ACTIVE,
    'ACCOUNTS.VALIDATION_ERROR_CODE_MESSAGES.ALREADY_ACTIVE',
  ],
  [
    ValidationErrorCodes.VALIDATION_ERROR_CODES_NO_COUNTRY_INFORMATION,
    'ACCOUNTS.VALIDATION_ERROR_CODE_MESSAGES.NO_COUNTRY_INFORMATION',
  ],
  [
    ValidationErrorCodes.VALIDATION_ERROR_CODES_UNSUPPORTED_COUNTRY,
    'ACCOUNTS.VALIDATION_ERROR_CODE_MESSAGES.UNSUPPORTED_COUNTRY',
  ],
  [
    ValidationErrorCodes.VALIDATION_ERROR_CODES_APP_IS_SUSPENDED,
    'ACCOUNTS.VALIDATION_ERROR_CODE_MESSAGES.APP_SUSPENDED',
  ],
  [
    ValidationErrorCodes.VALIDATION_ERROR_CODES_INVALID_EDITION,
    'ACCOUNTS.VALIDATION_ERROR_CODE_MESSAGES.INVALID_EDITION',
  ],
]);

// List containing all valid Pending Activation Statuses
const PendingActivationStatuses: PendingActivationStatus[] = [
  PendingActivationStatus.PENDING,
  PendingActivationStatus.REJECTED,
];

// Default page size for fetching Order Items for business
const DEFAULT_PAGE_SIZE = 50;

@Injectable({
  providedIn: 'root',
})
export class PendingActivationsService {
  constructor(
    private accountService: AccountService,
    private orderFulfillmentService: OrderFulfillmentService,
    private translate: TranslateService,
    private marketplaceAppService: MarketplaceAppService,
  ) {}

  getPendingActivationsForBusiness(businessId: string): Observable<PendingActivationDisplay[]> {
    const pendingActivations$ = this.accountService.listPendingActivationsForBusiness(businessId).pipe(
      map((acts) => {
        return acts.filter(this.pendingActivationIsValid);
      }),
    );

    const pendingOrderItems$ = this.getOrderItemsForBusiness(businessId).pipe(
      map((orderItems: OrderItem[]) => {
        return orderItems.filter(this.orderItemIsValid);
      }),
    );

    // Remove duplicate entries.
    const filteredOrderItems$: Observable<OrderItem[]> = combineLatest([pendingOrderItems$, pendingActivations$]).pipe(
      map(([orderItems, activations]) => {
        return orderItems.filter((oi) => {
          return !activations.some((act) => {
            const activation = act.addonId ? act.addonActivation : act.appActivation;
            return oi.activationId === act.activationId || oi.orderItemId === activation.billingOrderId;
          });
        });
      }),
    );

    // Blend results of order fulfillment service and accounts service together
    return combineLatest([pendingActivations$, filteredOrderItems$]).pipe(
      switchMap(async ([activations, orderItems]) => {
        const activationsDisplay = activations.map(this.convertPendingActivationToDisplay);
        const orderItemsDisplay = await Promise.all(
          orderItems.map(async (orderItem) => this.convertOrderItemToDisplay(orderItem)),
        );
        return [...activationsDisplay, ...orderItemsDisplay];
      }),
      map((displayActivations) => {
        return displayActivations.sort((a, b) => {
          return b.createdDate.getTime() - a.createdDate.getTime();
        });
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  /**
   * Get order items for a business, by calling to OrderFulfillmentService.
   *
   * @param businessId - ID of the business to get order items for.
   * @param [cursor] - Cursor of page to list
   * @param [prevOrderItems] - Size of page to list
   */
  private getOrderItemsForBusiness(
    businessId: string,
    cursor?: Cursor,
    prevOrderItems?: OrderItem[],
  ): Observable<OrderItem[]> {
    return this.orderFulfillmentService.listOrderItemsForBusiness(businessId, cursor, DEFAULT_PAGE_SIZE).pipe(
      map((response: PagedOrderItemsResponse) => {
        let allOrderItems = prevOrderItems || [];

        const { orderItems, hasMore, nextCursor } = response;

        if (orderItems) {
          allOrderItems = allOrderItems.concat(orderItems);
        }

        return [allOrderItems, nextCursor, hasMore];
      }),
      switchMap(([allOrderItems, nextCursor, hasMore]: [OrderItem[], Cursor, boolean]) => {
        if (hasMore) {
          return this.getOrderItemsForBusiness(businessId, nextCursor, allOrderItems);
        } else {
          return of(allOrderItems);
        }
      }),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
  }

  /**
   * Return the pendingActivations$ Observable.
   *
   * @return The combined OrderFulfillment and Accounts pending activations.
   */
  public getPendingActivations$(): Observable<PendingActivationDisplay[]> {
    return this.accountService.accountID$.pipe(
      switchMap((businessId: string) => {
        return this.getPendingActivationsForBusiness(businessId);
      }),
    );
  }

  /**
   * Convert a pending activation in Accounts to a PendingActivationDisplay item.
   *
   * @param item - Pending activation to convert.
   * @return The PendingActivationDisplay item with extracted details.
   */
  convertPendingActivationToDisplay = (item: PendingActivation): PendingActivationDisplay => {
    const {
      activationId,
      appId,
      addonId,
      appActivation,
      addonActivation,
      businessId,
      createdDate,
      updatedDate,
      status,
      rejectedReason,
    } = item;

    const isAddon = !!addonId;
    const activation = isAddon ? addonActivation : appActivation;
    const editionId = isAddon ? '' : (activation as Account).editionId;

    return {
      activationId,
      businessId,
      itemId: isAddon ? addonId : appId,
      editionId,
      orderFormSubmissionId: activation.orderFormSubmissionId,
      createdDate,
      updatedDate,
      status,
      pendingActivationStep: this.getPendingActivationPendingStep(item),
      rejectedReason,
      appId,
      addonId,
    };
  };

  /**
   * Convert a pending OrderFulfillment OrderItem to a PendingActivationDisplay item.
   *
   * @param item - Order Item to convert.
   * @return The PendingActivationDisplay item with extracted details.
   */
  convertOrderItemToDisplay = async (item: OrderItem): Promise<PendingActivationDisplay> => {
    const {
      activationId,
      businessId,
      itemId,
      editionId,
      orderFormSubmissionId,
      created,
      updated,
      status,
      orderId,
      orderItemId,
      validationErrorCodes,
    } = item;

    const isRejected = OrderItemRejectedStatuses.indexOf(status) > -1;
    const convertedStatus = isRejected ? PendingActivationStatus.REJECTED : PendingActivationStatus.PENDING;

    let rejectedReason;
    if (isRejected) {
      if (status === OrderItemStatus.ORDER_ITEM_STATUS_CHARGE_FAILED && item.chargeFailureCode) {
        rejectedReason = this.getChargeFailedReason(item.chargeFailureCode);
      } else {
        rejectedReason = validationErrorCodes
          ? this.getRejectionReasonFromValidationErrorCodes(validationErrorCodes)
          : RejectedOrderItemText.get(status);
      }
    }

    const app = await firstValueFrom(this.marketplaceAppService.getApp(itemId));
    const appId =
      app.type === AppType.APP_TYPE_APP || !app.type
        ? app.appId
        : app.restrictions?.appRequirement?.rules?.[0]?.appIds?.[0];
    const addonId = app.type === AppType.APP_TYPE_ADDON ? app.appId : null;

    return {
      activationId,
      businessId,
      createdDate: created,
      editionId,
      itemId,
      orderFormSubmissionId,
      rejectedReason,
      status: convertedStatus,
      pendingActivationStep: this.getOrderItemPendingStep(item),
      updatedDate: updated,
      orderId,
      orderItemId,
      appId,
      addonId,
    };
  };

  /**
   * Obtain rejection reasons from Order Item Validation Error codes
   *
   * @param validationErrorCodes - The validation error codes for an order item.
   * @return A string containing error code messages based on what codes are on a given order item.
   */
  getRejectionReasonFromValidationErrorCodes = (validationErrorCodes: ValidationErrorCodes[]): string => {
    const validationErrorText = validationErrorCodes
      .map((code) => {
        const untranslatedErrorText = ValidationErrorOrderItemText.get(code);
        return untranslatedErrorText ? this.translate.instant(untranslatedErrorText) : '';
      })
      .filter((errorText) => errorText);
    return validationErrorText && validationErrorText.length > 0
      ? validationErrorText.join('\n\n')
      : RejectedOrderItemText.get(OrderItemStatus.ORDER_ITEM_STATUS_VALIDATION_FAILED);
  };

  /**
   * Returns specific reason for stripe declining the charge for the activation.
   *
   * @param failureCode - the Code saved on the order item after the charge failed.
   */
  getChargeFailedReason(failureCode: string): string {
    return (
      DeclineReasons.get(failureCode)?.description ||
      RejectedOrderItemText.get(OrderItemStatus.ORDER_ITEM_STATUS_CHARGE_FAILED)
    );
  }

  /**
   * Returns the current PendingActivationStep given an order item
   * @param oi - the order item to get the step for
   */
  getOrderItemPendingStep(oi: OrderItem): PendingActivationStep {
    switch (oi.status) {
      case OrderItemStatus.ORDER_ITEM_STATUS_PENDING_ACTIVATION:
        return PendingActivationStep.WAITING_ON_VENDOR;
      case OrderItemStatus.ORDER_ITEM_STATUS_VALIDATION_FAILED:
        return PendingActivationStep.VALIDATION_FAILED;
      case OrderItemStatus.ORDER_ITEM_STATUS_CHARGE_FAILED:
        return PendingActivationStep.CHARGE_FAILED;
      case OrderItemStatus.ORDER_ITEM_STATUS_ACTIVATION_FAILED:
        return PendingActivationStep.ACTIVATION_FAILED;
    }
  }

  /**
   * Returns the current PendingActivationStep given a pending activation
   * @param pa - the pending activation to get the step for
   */
  getPendingActivationPendingStep(pa: PendingActivation): PendingActivationStep {
    switch (pa.status) {
      case PendingActivationStatus.PENDING:
        return PendingActivationStep.WAITING_ON_VENDOR;
      case PendingActivationStatus.REJECTED:
        return PendingActivationStep.ACTIVATION_FAILED;
    }
  }

  /**
   * Determine whether or not a pending activation is in a pending or rejected state, for display purposes.
   *
   * @param item - Pending activation to determine pending state.
   * @returns True if the pending activation is in a state that should be presented as pending.
   */
  pendingActivationIsValid(activation: PendingActivation): boolean {
    return PendingActivationStatuses.indexOf(activation.status) > -1;
  }

  /**
   * Determine whether or not an order item is in a pending or rejected state, for display purposes.
   *
   * @param item - Order item to determine pending state.
   * @returns True if the order item is in a state that should be presented as pending.
   */
  orderItemIsValid(item: OrderItem): boolean {
    return OrderItemPendingStatuses.indexOf(item.status) > -1 || OrderItemRejectedStatuses.indexOf(item.status) > -1;
  }
}
